import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiChevronRight } from 'react-icons/fi';
import * as translator from '../../../../../utils/translator';
import { useCartContext } from '../../../../../customHooks/useCart';
import { careProfileAsset } from '../careProfileAssets';
import { MainTracker } from '../../../../../PixelTrackers/MainTracker';
import { fillWeight, WeightUnit } from '../../../../../utils/weightUnits';

interface PlanEndedStateProps {
	language: string;
	/** Kilograms left to the customer's goal (progress.toGoalKg). Null/0 hides the goal sentence. */
	toGoalKg: number | null;
	weightUnit?: WeightUnit;
	isMonthlyPlan?: boolean;
}

const t = (language: string, key: string, fallback: string): string => {
	const value = translator.translate(language, 'my_account', key);
	if (typeof value !== 'string' || !value || value === key || value.includes(key)) {
		return fallback;
	}
	return value;
};

// Figma paints the heading and the "kg from your goal" fragment with the Care+ orange gradient.
const GRADIENT_TEXT_STYLE: React.CSSProperties = {
	backgroundImage: 'linear-gradient(90deg, #f59200 0%, #ffbb29 100%)',
	WebkitBackgroundClip: 'text',
	backgroundClip: 'text',
	WebkitTextFillColor: 'transparent',
	color: 'transparent',
};

/**
 * Term-end renew card. Leads the left column (desktop) / the page (mobile) while every other
 * dashboard feature is locked behind it.
 *
 * Renew re-enters the Care+ funnel at the questionnaire — the only step that mints a cart — so
 * Choose Treatment can write a plan-mapped medication and Choose Plan receives a non-empty tier
 * matrix. The previous `/care_plus/...` intro carousel jumped straight to Choose Plan with no
 * cart, which is why it offered only the one-time / prescription-only fallback and then failed
 * to persist the choice.
 */
const PlanEndedState: React.FC<PlanEndedStateProps> = ({ language, toGoalKg, weightUnit = 'kg', isMonthlyPlan }) => {
	const navigate = useNavigate();
	const { country } = useParams<{ country: string }>();
	const { clearCart } = useCartContext();
	const countryID = country ?? language;

	const handleRenew = () => {
		MainTracker.track('custom', 'Renew Plan Clicked', {});
		// Drop the expired subscription's cart id/token first: the questionnaire submit forwards
		// the CART_ORDER headers, and a stale cart would carry its old plan / delivery flags along.
		clearCart();
		navigate(`/questionnaire/${countryID}/${language}/weight_loss`);
	};

	const hasGoal = toGoalKg !== null && toGoalKg > 0;
	const bodyPrefix = t(language, 'CareProfile/States/Ended/BodyPrefix', "You're");
	const bodyGoalTemplate = t(language, 'CareProfile/States/Ended/BodyGoal', '{kg} kg from your goal');
	const bodyGoal = hasGoal ? fillWeight(bodyGoalTemplate, '{kg}', toGoalKg!, weightUnit) : bodyGoalTemplate.replace('{kg}', '');
	const bodySuffix = t(
		language,
		'CareProfile/States/Ended/Body',
		'Renew to keep your prescriber, specialist and medication support going.',
	);

	return (
		<div
			id="care-profile-plan-ended-card"
			className="relative flex flex-col overflow-hidden rounded-[20px] border border-solid border-[#dee3ed] bg-[#f8f8f8]"
		>
			{/* Mobile: copy beside the illustration. Desktop: copy above it. */}
			<div className="flex flex-row lg:flex-col">
				<div className="flex w-[45%] shrink-0 flex-col gap-y-1 px-[22px] pb-4 pt-[34px] lg:w-full lg:gap-y-2 lg:px-6 lg:pb-0 lg:pt-8">
					<h3 className="m-0 text-[19px] font-semibold leading-[1.2] lg:text-[28px] lg:leading-[32px]" style={GRADIENT_TEXT_STYLE}>
						{t(language, isMonthlyPlan ? 'CarePlus/monthlyPlan/planEndedTitle' : 'CareProfile/States/Ended/Title', 'Your plan has ended')}
					</h3>
					<p className="m-0 text-[12px] leading-[1.4] text-[#616975] lg:text-[18px] lg:leading-[1.3]">
						{hasGoal && (
							<>
								{bodyPrefix}{' '}
								<span className="font-semibold lg:font-bold" style={GRADIENT_TEXT_STYLE}>
									{bodyGoal}
								</span>
								<span style={GRADIENT_TEXT_STYLE}>.</span>{' '}
							</>
						)}
						{bodySuffix}
					</p>
				</div>
				{/* The CDN asset is the full-body photo (2:3, tablet included); Figma shows only a
				    head-and-shoulders crop, ~84% of the card width, right-aligned and cut off by the
				    CTA. Anchoring the image to the top of a 200px box reproduces that crop. */}
				<div className="relative min-h-[227px] flex-1 lg:mt-3 lg:h-[200px] lg:min-h-0 lg:w-full lg:flex-none">
					<img
						id="care-profile-state-banner-ended-image"
						src={careProfileAsset('plan-ended-illustration')}
						alt=""
						className="pointer-events-none absolute inset-0 h-full w-full object-cover object-top lg:left-auto lg:w-[84%]"
					/>
				</div>
			</div>
			<button
				id="care-profile-restart-cta"
				type="button"
				onClick={handleRenew}
				className="flex h-[40px] w-full cursor-pointer items-center justify-center gap-x-1 border-none bg-care-plus-button-gradient text-[14px] font-medium text-white transition-all duration-200 hover:bg-care-plus-button-gradient-hover lg:h-[46px]"
			>
				{t(language, 'CareProfile/States/Ended/RenewCta', 'Renew my plan')}
				<FiChevronRight size={13} />
			</button>
		</div>
	);
};

export default PlanEndedState;
